// The bookmark of a card, connected: a click sends PUT or DELETE
// /me/saved/{origin}/{item_id} through useSaveItem (the lists in the cache
// change at once and go back if the api refuses). A refused change stays
// here, as the favourites' do (SourcesPanel), until the user sends it again
// with «Riprova» or chooses again on the same card. It lives in the list's
// component: changing tab unmounts it and the warning with it (accepted for
// the internal release; the cache is already back as the api has it).
import { useRef, useState } from "react";

import type { NewsItem } from "../../data/mock";
import { parseItemId, type SavedChange } from "./adapt";
import { useSaveItem } from "./api";

export const SAVE_FAILED = "Salvataggio non riuscito.";

const itemKey = (change: SavedChange) => `${change.origin}/${change.id}`;

export interface Bookmarks {
  /** The bookmark of a card the other way round. */
  toggle: (news: NewsItem) => void;
  /** The changes the api refused and nothing has overridden: the message and how to send them again. */
  failure: { message: string; onRetry: () => void } | null;
}

export function useBookmarks(): Bookmarks {
  const save = useSaveItem();
  const [rejected, setRejected] = useState<SavedChange[]>([]);
  // for each item, the sequence number of the last change that touched it
  const lastTouch = useRef(new Map<string, number>());
  const sequence = useRef(0);

  // mutateAsync, not mutate: the per-call callbacks of mutate only fire for the last call,
  // and several cards can be in flight at once
  const send = (change: SavedChange) => {
    const key = itemKey(change);
    sequence.current += 1;
    const mine = sequence.current;
    lastTouch.current.set(key, mine);
    // a new choice on a card supersedes any refused one on it (this is also how a retry takes its entry out)
    setRejected((list) => list.filter((c) => itemKey(c) !== key));
    save.mutateAsync(change).catch(() => {
      // keep it only when nobody has chosen again on this card while it was in flight
      if (lastTouch.current.get(key) !== mine) return;
      setRejected((list) => [...list.filter((c) => itemKey(c) !== key), change]);
    });
  };

  const toggle = (news: NewsItem) => {
    const ref = parseItemId(news.id);
    // every card of the connected lists has an id from itemId: one that does not parse cannot be saved
    if (!ref) return;
    send({ ...ref, saved: !news.saved });
  };

  const failure =
    rejected.length > 0 ? { message: SAVE_FAILED, onRetry: () => rejected.forEach((change) => send(change)) } : null;

  return { toggle, failure };
}
